import { LoadingManager } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";

import { publicUrl } from "@core/publicUrl";
import { installGltfImageDedupPlugin } from "./gltfImageDedup";
import { installTextureBudgetOnParser } from "./textureBudget";

/**
 * `gltfLoaderFactory` — a shared `GLTFLoader` configured with a `DRACOLoader`.
 *
 * Cloud (Pixyz) conversions and many optimized GLBs use `KHR_draco_mesh_compression`;
 * the bare `GLTFLoader` throws "unsupported compression" on them. Unity's GLTFast
 * decodes Draco natively, so the web must too. The Draco decoder (WASM) is served
 * from `public/draco/` (copied from `three/examples/jsm/libs/draco/gltf`), same
 * origin — no CDN / external fetch.
 *
 * Used by both the import parse path (`WebImporterModel`) and the render cache
 * (`GltfAssetCache`), since the persisted asset IS the Draco GLB.
 */

const K_DRACO_DECODER_PATH = publicUrl("draco/");

let s_dracoLoader: DRACOLoader | null = null;

function sharedDracoLoader(): DRACOLoader {
  if (!s_dracoLoader) {
    s_dracoLoader = new DRACOLoader();
    s_dracoLoader.setDecoderPath(K_DRACO_DECODER_PATH);
  }
  return s_dracoLoader;
}

/**
 * A `GLTFLoader` that can decode Draco-compressed GLBs. Pass a
 * `LoadingManager` (with a `setURLModifier`) when external resource URIs
 * must resolve to unpacked blob URLs — the `.gltfzip` read path does this.
 * The shared `DRACOLoader` keeps its own default manager for the decoder
 * WASM fetch, so per-load managers don't affect it.
 */
export function createGltfLoader(manager?: LoadingManager): GLTFLoader {
  const loader = new GLTFLoader(manager);
  loader.setDRACOLoader(sharedDracoLoader());
  // Texture budget (see `textureBudget.ts`): swap the parser's eager
  // ImageBitmap decoder for the serialized, dimension-capped one. Registered
  // here so EVERY parse — render cache, import, Pixyz output — is covered;
  // `GLTFLoader.register` hands the callback the live parser, which is the
  // one supported seam that can reach `parser.textureLoader`.
  loader.register((parser) => {
    installTextureBudgetOnParser(parser);
    return { name: "CAMPFIRE_texture_budget" };
  });
  // Identical embedded images decode once (see `gltfImageDedup.ts`). Sibling
  // to the dimension cap above: 77 copies of a 2048² JPEG sit at the mobile
  // budget and still jetsam an iPhone unless the sources are remapped before
  // `loadImageSource`. `beforeRoot` needs the GLB bufferViews, so this is a
  // plugin rather than a loader-construction swap.
  loader.register((parser) => installGltfImageDedupPlugin(parser));
  return loader;
}
